import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../services/api.service';
import { AreaFilterService } from '../services/area-filter.service';
import { BankAmount, BankAmountRequest, BankAmountUpdateResponse } from '../model/bank.model';
import { Employee } from '../model/employee.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AccountUploadDialog } from '../dialogs/account-upload/account-upload.component';
import { MerchantUploadDialog } from '../dialogs/merchant-upload/merchant-upload.component';
import { MerchantUploadViewDialog } from '../dialogs/merchant-upload-view/merchant-upload-view.component';
import { AccountUploadViewDialog } from '../dialogs/account-upload-view/account-upload-view.component';

@Component({
  selector: 'app-bank',
  templateUrl: './bank.component.html',
  styleUrls: ['./bank.component.scss']
})
export class BankComponent implements OnInit {
  bankForm: FormGroup;
  employees: Employee[] = [];
  collectorEmployees: Employee[] = [];
  bankAmounts: BankAmount[] = [];
  filteredBankAmounts: BankAmount[] = [];
  loading = false;
  editingBankId: number | null = null;
  searchQuery: string = '';

  // Date cycle search properties
  availableCycles: { label: string; startDate: string; endDate: string }[] = [];
  selectedCycleControl = new FormControl('');
  showCycleSelector: boolean = false;
  currentCycleStart: string = '';
  currentCycleEnd: string = '';

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private areaFilterService: AreaFilterService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.bankForm = this.fb.group({
      employeeId: ['', Validators.required],
      bankAmount: ['', [Validators.required, Validators.min(0)]],
      bankDate: ['']
    });
  }

  ngOnInit(): void {
    this.initializeAvailableCycles();
    this.loadEmployees();
  }

  private initializeAvailableCycles(): void {
    const today = new Date();
    const cycles: { label: string; startDate: string; endDate: string }[] = [];
    const seenLabels = new Set<string>();
    
    // Generate last 12 cycles: each cycle runs from the 26th of one month to the 25th of the next month
    for (let i = 0; i < 12; i++) {
      const cycleDate = new Date(today.getFullYear(), today.getMonth() - i, 15);
      let cycleStart = new Date(cycleDate.getFullYear(), cycleDate.getMonth(), 26);
      let cycleEnd = new Date(cycleDate.getFullYear(), cycleDate.getMonth() + 1, 25);
      
      // If the cycle start is still in the future, shift back one month
      if (cycleStart > today) {
        cycleStart = new Date(cycleStart.getFullYear(), cycleStart.getMonth() - 1, 26);
        cycleEnd = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth() - 1, 25);
      }
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `${monthNames[cycleStart.getMonth()]} ${cycleStart.getFullYear()} Cycle`;
      
      if (seenLabels.has(label)) {
        continue;
      }
      seenLabels.add(label);
      
      cycles.push({
        label,
        startDate: this.formatDate(cycleStart),
        endDate: this.formatDate(cycleEnd)
      });
    }
    
    this.availableCycles = cycles;
    
    // Set default to current cycle (first item)
    if (cycles.length > 0) {
      this.selectedCycleControl.setValue(cycles[0].label);
      this.currentCycleStart = cycles[0].startDate;
      this.currentCycleEnd = cycles[0].endDate;
    }
  }

  onCycleChange(selectedLabel: string): void {
    const selectedCycle = this.availableCycles.find(cycle => cycle.label === selectedLabel);
    if (selectedCycle) {
      this.selectedCycleControl.setValue(selectedLabel);
      this.currentCycleStart = selectedCycle.startDate;
      this.currentCycleEnd = selectedCycle.endDate;
      this.loadBankAmounts();
    }
  }

  toggleCycleSelector(): void {
    this.showCycleSelector = !this.showCycleSelector;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadEmployees(): void {
    this.loading = true;
    this.apiService.getEmployees().subscribe({
      next: (response) => {
        this.employees = this.areaFilterService.filterEmployeesByArea(response.employees || []);
        // Filter employees who have collector role in their roles array
        this.collectorEmployees = this.employees.filter(emp => 
          emp.roles && emp.roles.some(role => role.roleName === 'collector')
        );
        this.loading = false;
        // Load bank amounts after employees are loaded
        this.loadBankAmounts();
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  loadBankAmounts(): void {
    this.loading = true;
    this.apiService.getBankAmountsByDateRange(this.currentCycleStart, this.currentCycleEnd).subscribe({
      next: (response: any) => {
        // API returns array directly, not wrapped in bankAmounts property
        const loadedBankAmounts = Array.isArray(response) ? response : (response.bankAmounts || []);
        console.log('Bank amounts loaded:', loadedBankAmounts);
        
        // Create a map of employeeId to bank amount for quick lookup
        const bankAmountMap = new Map<number, BankAmount>();
        loadedBankAmounts.forEach((bank: BankAmount) => {
          if (bank.employeeId) {
            bankAmountMap.set(bank.employeeId, bank);
          }
        });
        
        // Create combined list showing all collector employees with their bank amounts
        this.bankAmounts = this.collectorEmployees.map(emp => {
          const existingBank = bankAmountMap.get(emp.employeeId);
          if (existingBank) {
            return existingBank;
          } else {
            // Return a bank amount entry with 0 for employees without bank amounts
            return {
              employeeId: emp.employeeId,
              bankAmount: 0,
              bankDate: null,
              id: null
            } as BankAmount;
          }
        });
        
        // Initialize filtered bank amounts
        this.filteredBankAmounts = [...this.bankAmounts];
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading bank amounts:', error);
        this.snackBar.open('Error loading bank amounts', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  getEmployeeName(employeeId: number): string {
    const employee = this.collectorEmployees.find(emp => emp.employeeId === employeeId);
    return employee ? employee.employeeName : 'Unknown';
  }

  onSearchChange(query: string): void {
    this.searchQuery = query.toLowerCase();
    if (!this.searchQuery) {
      this.filteredBankAmounts = [...this.bankAmounts];
      return;
    }
    
    this.filteredBankAmounts = this.bankAmounts.filter(bank => {
      const employee = this.collectorEmployees.find(emp => emp.employeeId === bank.employeeId);
      if (!employee) return false;
      
      const employeeName = employee.employeeName.toLowerCase();
      const employeeId = bank.employeeId.toString();
      
      return employeeName.includes(this.searchQuery) || employeeId.includes(this.searchQuery);
    });
  }

  onSubmit(): void {
    if (this.bankForm.invalid) {
      this.snackBar.open('Please fill all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    const request: BankAmountRequest = {
      id: this.editingBankId ?? undefined,
      employeeId: this.bankForm.value.employeeId,
      bankAmount: this.bankForm.value.bankAmount,
      bankDate: this.bankForm.value.bankDate || this.currentCycleStart,
      cycleStartDate: this.currentCycleStart,
      cycleEndDate: this.currentCycleEnd
    };

    // Use the same POST save API for both create and update
    this.apiService.saveBankAmount(request).subscribe({
      next: (response: any) => {
        if (response) {
          this.snackBar.open(
            this.editingBankId ? 'Bank amount updated successfully' : 'Bank amount saved successfully',
            'Close',
            { duration: 3000 }
          );
          this.resetForm();
          this.loadBankAmounts();
        } else {
          this.snackBar.open(
            this.editingBankId ? 'Error updating bank amount' : 'Error saving bank amount',
            'Close',
            { duration: 3000 }
          );
        }
      },
      error: (error) => {
        console.error(this.editingBankId ? 'Error updating bank amount:' : 'Error saving bank amount:', error);
        this.snackBar.open(
          this.editingBankId ? 'Error updating bank amount' : 'Error saving bank amount',
          'Close',
          { duration: 3000 }
        );
      }
    });
  }

  editBank(bank: BankAmount): void {
    this.editingBankId = bank.id || null;
    this.bankForm.patchValue({
      employeeId: bank.employeeId,
      bankAmount: bank.bankAmount,
      bankDate: bank.bankDate
    });
    this.snackBar.open('Editing bank amount', 'Close', { duration: 2000 });
  }


  
  resetForm(): void {
    this.bankForm.reset();
    this.editingBankId = null;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }

  openAccountUploadDialog(employeeId: number): void {
    const dialogRef = this.dialog.open(AccountUploadDialog, {
      width: '600px',
      data: {
        employeeId: employeeId,
        cycleStart: this.currentCycleStart,
        cycleEnd: this.currentCycleEnd
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Detailed success/partial/failure feedback is shown inside the dialog itself.
        // Refresh the bank amounts so any newly uploaded data is reflected.
        this.loadBankAmounts();
      }
    });
  }

  openMerchantUploadDialog(employeeId: number): void {
    const dialogRef = this.dialog.open(MerchantUploadDialog, {
      width: '600px',
      data: {
        employeeId: employeeId,
        cycleStart: this.currentCycleStart,
        cycleEnd: this.currentCycleEnd
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Detailed success/partial/failure feedback is shown inside the dialog itself.
        // Refresh the bank amounts so any newly uploaded data is reflected.
        this.loadBankAmounts();
      }
    });
  }

  viewAccountUploadData(employeeId: number): void {
    const dialogRef = this.dialog.open(AccountUploadViewDialog, {
      width: '95vw',
      maxWidth: '1400px',
      data: {
        employeeId: employeeId,
        cycleStart: this.currentCycleStart,
        cycleEnd: this.currentCycleEnd
      }
    });
  }

  viewMerchantUploadData(employeeId: number): void {
    const dialogRef = this.dialog.open(MerchantUploadViewDialog, {
      width: '95vw',
      maxWidth: '1400px',
      data: {
        employeeId: employeeId,
        cycleStart: this.currentCycleStart,
        cycleEnd: this.currentCycleEnd
      }
    });
  }
}
